// FP 보정 간트 — 하단 "작업 상세 보정" 탭 (old `adjustmentUtils/tabs/AdjustDetailTab.jsx`)
//
// old는 표 3개가 나란히 있었다: ① 작업 속성(자원/품목/시작/종료/수량) ② 자원 이동(대체자원 + 시작시각)
// ③ 확장속성 attr01~10. 이관 범위는 ①의 **표시**와 ②의 **이동 실행**이다 — 편집 결과를 저장할 경로가
// old·new 백엔드 양쪽에 없어서(§6-1) ①③의 직접 편집은 화면 안에서만 도는 값이 되고, ②만으로도
// "자원과 시작시각을 지정해 옮긴다"는 목적이 달성된다(§6-4의 상세탭 "적용" 항목).
//
// 값의 출처는 **선택된 박스**다(서버 재조회 없음) — 편집 즉시 반영되어야 하므로 클라이언트 SSOT를
// 그대로 읽는다(§6-6). old도 `runBox.Start`/`activityData`를 읽었지 서버를 치지 않았다.
//
// TODO(MIG-T2): [FP_ADJUST_GANTT] 상세탭의 **직접 편집 2종**(시작/종료/수량 입력 적용, 확장속성
//   attr01~10 적용) 미이관.
//   old: `applyAdjustData()` / `applyAttrData()` → `updateActivity`/`updateActivityData`
//   사유: 저장 경로가 없어(§6-1) 화면 안에서만 도는 값이 된다. 시각 지정 이동은 아래 "자원 이동"
//         폼이 같은 목적을 달성하고, 수량·확장속성은 계획 재계산 없이 바꾸면 오히려 값이 어긋난다.

import { useEffect, useMemo, useState } from "react";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";

import { GanttDetailGroup } from "@plannel/pages/factory-plan/common/gantt/GanttDetailRows";
import { formatCodeName, formatQty } from "@plannel/pages/factory-plan/common/gantt/ganttFormat";
import dateUtils from "@plannel/utils/date-util";

const FIELD_WIDTH = 230;

const AdjustDetailTab = ({ t, box, row, altResources = [], onMove, disabled = false }) => {
  const [moveResourceCd, setMoveResourceCd] = useState("");
  const [moveStartTs, setMoveStartTs] = useState(null);

  // 선택이 바뀌면 이동 폼을 비운다(old `resetForMove()`) — 직전 작업에 입력한 값이 남아 있으면
  // 다른 작업을 고른 뒤 "이동"을 눌렀을 때 의도하지 않은 곳으로 간다.
  useEffect(() => {
    setMoveResourceCd("");
    setMoveStartTs(box ? new Date(box.s) : null);
  }, [box]);

  // 대체자원 목록에서 **현재 자원은 뺀다**(old도 `activity.Row.resourceCd !== altResource.resourceCd`).
  const options = useMemo(
    () => altResources.filter(alt => alt.resourceCd !== row?.resourceCd),
    [altResources, row]
  );

  const activity = box?.activity;
  // 계획 구간 밖으로 걸친 활동은 old에서 박스 `Type:'Fixed'`(= 이동·리사이즈 불가)였다 — 그 잠금을
  // 여기서도 지킨다(§6-2 박스 Type 표).
  const locked = disabled || !box || box.inBound === false;

  const rows = useMemo(() => {
    if (!box) return [];
    return [
      { labelKey: "FP_RESOURCE", value: formatCodeName(row?.resourceCd, row?.resourceNm) },
      { labelKey: "FP_ITEM", value: formatCodeName(activity?.itemCd, activity?.itemNm) },
      { labelKey: "FP_START_TS", value: dateUtils.formatDateTime(new Date(box.s)) },
      { labelKey: "FP_END_TS", value: dateUtils.formatDateTime(new Date(box.e)) },
      { labelKey: "FP_QTY", value: formatQty(activity?.qty, activity?.itemUom) }
    ];
  }, [box, row, activity]);

  if (!box) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">{t("MSG_NoSelectedData")}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", gap: 4, px: 2, py: 1.5, height: "100%", overflow: "auto" }}>
      <Box sx={{ minWidth: 300 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>{t("FP_PLAN_DETAIL_ADJUST")}</Typography>
        <GanttDetailGroup rows={rows} t={t} />
      </Box>

      {/* 자원 이동 — old `setResourceAdjustTable()` + `moveResource()`.
          대체자원이 없으면 폼 전체가 잠긴다(old `resMoveDisabled`). */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, minWidth: FIELD_WIDTH + 40 }}>
        <Typography variant="subtitle2">{t("FP_MOVE_RESOURCE")}</Typography>
        <TextField
          select
          size="small"
          label={t("FP_RESOURCE")}
          value={moveResourceCd}
          disabled={locked || options.length === 0}
          onChange={(event) => setMoveResourceCd(event.target.value)}
          sx={{ width: FIELD_WIDTH }}
        >
          {options.map(option => (
            <MenuItem key={option.resourceCd} value={option.resourceCd}>
              {option.resourceNm ?? option.resourceCd}
            </MenuItem>
          ))}
        </TextField>

        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DateTimePicker
            format="yyyy-MM-dd HH:mm"
            label={t("FP_START_TS")}
            value={moveStartTs}
            disabled={locked || options.length === 0}
            onChange={(value) => setMoveStartTs(value)}
            slotProps={{ textField: { size: "small", sx: { width: FIELD_WIDTH } } }}
          />
        </LocalizationProvider>

        <Button
          variant="contained"
          size="small"
          disabled={locked || options.length === 0 || !moveResourceCd || !moveStartTs}
          onClick={() => onMove?.({ resourceCd: moveResourceCd, startTs: moveStartTs.getTime() })}
          sx={{ alignSelf: "flex-start" }}
        >
          {t("apply")}
        </Button>
      </Box>
    </Box>
  );
};

export default AdjustDetailTab;
